import {
  VIEW_PESTS_DEGREE_DAYS_ID_insectId,
  VIEW_PESTS_TRAP_CATCHES_ID_insectId,
} from '@semios/app-platform-value-type-definitions'
import { PEST_CONFIGS } from 'App/dashboards/_utils/pestDashboardConfig'
import { useMemo } from 'react'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { checkAuthorization } from 'utils/checkAuthorization'
import { sortByKey } from 'utils/sortByKey'

export type TPropertyOption = { value: string; label: string; group?: string }

// Whether the user can see degree days or trap catches for a specific pest on a property — the
// permission gate for both the per-board dropdown (useDashboardPreferences) and the section list.
export const hasPestDataAccess = (propertyId: number, insectId: number): boolean =>
  checkAuthorization({ permission: VIEW_PESTS_DEGREE_DAYS_ID_insectId(insectId), entity: propertyId }) ||
  checkAuthorization({ permission: VIEW_PESTS_TRAP_CATCHES_ID_insectId(insectId), entity: propertyId })

// Pests with a dashboard — a property is listed (across the section) only if the user has access to
// at least one of them on that property. Individual boards narrow this to their own pest.
const LISTED_INSECT_IDS = PEST_CONFIGS.map((c) => c.insectId)

const hasAnyPestDataAccess = (propertyId: number): boolean =>
  LISTED_INSECT_IDS.some((insectId) => hasPestDataAccess(propertyId, insectId))

// The per-board selection + date range live in useDashboardPreferences (persisted per dashboard);
// this hook only supplies the section-wide property option list (the dropdown source).
export type TDashboardPropertyOptions = {
  propertyOptions: TPropertyOption[]
}

export const useDashboardPropertyOptions = (): TDashboardPropertyOptions => {
  const properties = fieldAssetStore.useSelector((s) => s.properties)
  // Recompute the (permission-filtered) options once permissions load, not just when properties do.
  const permissions = userDetailsStore.useSelector((s) => s.permissions)

  const propertyOptions = useMemo<TPropertyOption[]>(() => {
    return (
      Object.entries(properties || {})
        .filter(([propertyId]) => hasAnyPestDataAccess(Number(propertyId)))
        .map(([propertyId, p]) => ({
          value: propertyId,
          label: p.propertyName,
        }))
        // Alphabetical by name, matching the other property dropdowns (sortByKey('propertyName')).
        .sort(sortByKey('label'))
    )
    // `permissions` is in the deps so the filter recomputes when permissions load — it's read
    // inside hasAnyPestDataAccess via checkAuthorization rather than referenced directly here.
  }, [properties, permissions])

  return useMemo(() => ({ propertyOptions }), [propertyOptions])
}
